import { useEffect, useState } from 'react';
import { apiGet, ApiError } from '../lib/apiClient';

/**
 * LynkPulse fleet health on the Dashboard (docs/administration.md §24.6.5).
 *
 * "Recently active" means "reported a reading within the staleness window", NOT a live
 * socket — the app syncs over BLE every 15 minutes, so this is fleet health rather
 * than a real-time per-user indicator. The window comes from the payload so this file
 * never has to agree with the backend about the number.
 */
export interface LynkPulseFleetMetrics {
  purchased: number;
  everPaired: number;
  recentlyActive: number;
  stale: number;
  purchasedNeverPaired: number;
  staleAfterMinutes: number;
}

export interface FleetTile {
  key: 'purchased' | 'recentlyActive' | 'stale' | 'purchasedNeverPaired';
  label: string;
  value: number;
  meta: string;
  /** True for the one tile that implies someone should do something. */
  actionable: boolean;
}

export function formatFleetTiles(m: LynkPulseFleetMetrics): FleetTile[] {
  return [
    {
      key: 'purchased',
      label: 'Bands purchased',
      value: m.purchased,
      meta: 'Paid for, whether or not ever paired',
      actionable: false,
    },
    {
      key: 'recentlyActive',
      label: 'Recently active',
      value: m.recentlyActive,
      meta: `Reported within ${m.staleAfterMinutes} min`,
      actionable: false,
    },
    {
      key: 'stale',
      label: 'Stale',
      value: m.stale,
      meta: `Paired once, silent for over ${m.staleAfterMinutes} min`,
      actionable: false,
    },
    // The one number that implies an action: a band paid for and never heard from.
    // gaps-and-flags records pairing failing at notify-enable (GATT 133) under weak
    // RSSI, so a real share of this cohort is expected once hardware ships.
    {
      key: 'purchasedNeverPaired',
      label: 'Never paired',
      value: m.purchasedNeverPaired,
      meta: 'Purchased but never reported — likely pairing failures',
      actionable: true,
    },
  ];
}

/**
 * Never-paired as a percentage of purchased, or null when nothing is purchased.
 *
 * Null rather than 0: with no bands sold, "0% failing" reads as good news about a
 * fleet that does not exist. That is production today.
 */
export function pairingFailureRate(m: LynkPulseFleetMetrics): number | null {
  if (m.purchased <= 0) return null;
  return Math.round((m.purchasedNeverPaired / m.purchased) * 100);
}

export function LynkPulseFleetPanel() {
  const [metrics, setMetrics] = useState<LynkPulseFleetMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiGet<LynkPulseFleetMetrics>('/admin/dashboard/lynkpulse-fleet')
      .then((data) => {
        if (!cancelled) setMetrics(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load LynkPulse fleet');
        }
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Mirrors Dashboard's own placeholder so the numeric slot never collapses. */
  const figure = (value: number | undefined) =>
    pending ? '…' : error && value === undefined ? '—' : (value ?? 0);

  const tiles: Array<Omit<FleetTile, 'value'> & { value: number | undefined }> = metrics
    ? formatFleetTiles(metrics)
    : [
        { key: 'purchased', label: 'Bands purchased', value: undefined, meta: '', actionable: false },
        {
          key: 'recentlyActive',
          label: 'Recently active',
          value: undefined,
          meta: '',
          actionable: false,
        },
        { key: 'stale', label: 'Stale', value: undefined, meta: '', actionable: false },
        {
          key: 'purchasedNeverPaired',
          label: 'Never paired',
          value: undefined,
          meta: '',
          actionable: true,
        },
      ];

  const rate = metrics ? pairingFailureRate(metrics) : null;

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>LynkPulse fleet</h2>
          {rate !== null && <p className="cell-sub">{rate}% purchased but never paired</p>}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="stat-card-grid">
        {tiles.map((tile) => (
          <div className="card stat-card" key={tile.key}>
            <div className="card-header">
              <div className="card-title">{tile.label}</div>
            </div>
            <div className="card-body">
              <span className="stat-card-value">{figure(tile.value)}</span>
              <span className="stat-card-meta">{tile.meta}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LynkPulseFleetPanel;
